import {
  IssueSyncDb, RowEnvelope, StoredSyncState, isQuotaExceeded,
} from './issue-sync-db';
import {
  IssueSyncRow, IssueSyncRowsResponse, IssueSyncScope, RECONCILE_INTERVAL_MS,
  RESIDENCY_LIMIT, ROWS_BY_ID_LIMIT, SYNC_PAGE_LIMIT, scopeKeyOf,
} from './issue-sync-models';
import {
  IssueSyncTransport, SyncCursorRejectedError, SyncScopeDeniedError,
} from './issue-sync-transport';
import { Clock, IssuePendingWrites, SettledWrite, systemClock } from './issue-pending-writes';

// The local replica of the Issues projection. It owns one question only — whether
// the resident rows are a correct picture of what the user is allowed to see — and
// deliberately not querying, filtering or sorting.
//
// Resident rows are open Issues. A closure arrives as a removal and the row leaves;
// closed history is always a server query and never enters the store.

export const SyncOutcomes = {
  Synced: 'synced', Declined: 'declined', Denied: 'denied', Failed: 'failed',
} as const;
export type SyncOutcome = typeof SyncOutcomes[keyof typeof SyncOutcomes];

export interface ScopeStatus {
  scopeKey: string;
  scope: IssueSyncScope;
  // False while the walk is still filling. Counts drawn from a filling scope
  // describe part of it, so the caller must label them rather than present them as
  // whole.
  complete: boolean;
  scopeTotal: number;
  // scopeTotal reached the server's cap and is a lower bound, not an exact count.
  scopeTotalIsFloor: boolean;
  // The scope holds more rows than the client will keep, so the list declines it
  // rather than filtering a silently narrowed subset.
  overThreshold: boolean;
  scopeVersion: string;
  asOfUtc: string;
}

export interface ReplicaEvents {
  // A scope's rows changed on disk. Carries ids, not rows: it crosses a
  // BroadcastChannel to other tabs, where a payload of whole rows would be waste.
  onScopeChanged?(scopeKey: string, rowIds?: string[], removedIds?: string[]): void;
  // The same change, carrying the rows themselves, for the consumer in this tab. A
  // query layer needs the row bodies, and it already has them here — making it re-read
  // the store to get back what was just written is the cost this avoids.
  onRowsApplied?(upserted: IssueSyncRow[], removedIds: string[]): void;
  // A write was rolled back because it never confirmed. The caller reports it.
  onWriteRolledBack?(writes: SettledWrite[]): void;
  onStatus?(status: ScopeStatus): void;
}

const idOf = (row: IssueSyncRow) => row.id;

export class IssueReplica {
  private readonly statuses = new Map<string, ScopeStatus>();
  // Applies run one at a time. Deltas must land in arrival order, and two
  // overlapping walks on one scope would interleave their cursor writes.
  private chain: Promise<unknown> = Promise.resolve();

  // Set when the owner is finished with this replica, which happens while a walk may
  // still be between pages. The feed is addressed by the tenant in force at the moment
  // the request goes out, so a walk that outlives its owner fetches the next tenant's
  // rows. Nothing it fetches after this point may be stored or announced.
  private stopped = false;

  constructor(
    private readonly db: IssueSyncDb<IssueSyncRow>,
    private readonly transport: IssueSyncTransport,
    readonly pendingWrites: IssuePendingWrites,
    private readonly ownerEvents: ReplicaEvents = {},
    private readonly clock: Clock = systemClock,
  ) {}

  // Final. A replica is built per tenant and is never restarted.
  stop(): void {
    this.stopped = true;
  }

  // Every announcement reaches the owner through here, so a stopped replica goes quiet
  // in one place rather than at each call site. Work already in flight still finishes;
  // it simply tells nobody, and the store keeps showing the tenant the user is on.
  private get events(): ReplicaEvents {
    return this.stopped ? {} : this.ownerEvents;
  }

  statusFor(scopeKey: string): ScopeStatus | null {
    return this.statuses.get(scopeKey) ?? null;
  }

  // Reads the rows a previous session left behind. An absent database or an evicted
  // store yields nothing, which starts a full re-sync — routine rather than
  // exceptional, because iOS clears storage after a week of not being opened.
  async hydrate(): Promise<IssueSyncRow[]> {
    const stored = await this.db.getAllSyncState();
    for (const state of stored) {
      this.statuses.set(state.scopeKey, this.toStatus(state));
      // Carry the reconciliation clock across a reload. Left unread, every page load
      // would restart the interval and a tab reopened often enough would never reach
      // it — which is the one thing that removes a row the user may no longer see.
      if (state.lastReconciledAtMs) {
        this.lastReconciledAt.set(state.scopeKey, state.lastReconciledAtMs);
      }
    }
    const rows = await this.db.getAllRows();
    const residents = rows.length;
    for (const [key, status] of this.statuses) {
      this.statuses.set(key, { ...status });
    }
    return rows.map(envelope => this.pendingWrites.patched(envelope.row));
  }

  // Brings a scope up to date, walking the feed until it reports complete. Safe to
  // call on a scope already synced: it resumes from the stored cursor.
  sync(scope: IssueSyncScope): Promise<SyncOutcome> {
    return this.serialize(() => this.runSync(scope));
  }

  // Reconciles every synced scope against the server's id set, and evicts any
  // resident row that no scope still claims.
  //
  // This is a permission mechanism. It is what stops a user keeping an Issue they may
  // no longer see: a row can leave scope through a group re-stamp, which produces no
  // removal and does not move scopeVersion, so no delta will ever mention it.
  reconcile(): Promise<boolean> {
    return this.serialize(() => this.runReconcile());
  }

  // True when a scope has gone long enough without reconciliation. The caller ticks
  // this rather than the replica owning a timer, so tests stay free of wall time.
  needsReconcile(): boolean {
    if (!this.statuses.size) return false;
    const now = this.clock.now();
    for (const status of this.statuses.values()) {
      // A declined scope holds no rows and is skipped by the pass itself, so it has no
      // timestamp and would otherwise answer "never reconciled" on every poll — asking for
      // a full id-set read of every other scope six times an hour instead of twice.
      if (status.overThreshold) continue;
      const last = this.lastReconciledAt.get(status.scopeKey);
      // A scope never reconciled always needs it. Treating "never" as a zero timestamp
      // would make the answer depend on how far the clock happens to be from its
      // origin rather than on whether the scope has actually been checked.
      if (last === undefined) return true;
      if (now - last >= RECONCILE_INTERVAL_MS) return true;
    }
    return false;
  }

  // Confirms the caller's own write without waiting for the ordinary poll. A failed
  // action left on screen for five minutes reads as applied, so this settles it in
  // seconds.
  //
  // Reads the touched rows by id. That route holds no cursor and so withholds nothing,
  // unlike the feed, which cannot release a row until it is older than the safety lag.
  // It must not advance a cursor either: these rows are not a delta and say nothing
  // about what else has changed.
  confirmWrites(issueIds: string[]): Promise<void> {
    return this.serialize(async () => {
      const unique = [...new Set(issueIds.filter(id => !!id))];
      if (!unique.length) return;

      for (let from = 0; from < unique.length; from += ROWS_BY_ID_LIMIT) {
        const chunk = unique.slice(from, from + ROWS_BY_ID_LIMIT);
        let response: IssueSyncRowsResponse;
        try {
          response = await this.transport.fetchRowsByIds(chunk);
        } catch {
          // Nothing is settled and nothing is evicted. The shadow keeps its deadline,
          // so a write whose confirmation cannot be read still fails honestly.
          continue;
        }
        await this.applyRows(response);
      }

      await this.rollBackExpired();
    });
  }

  // Applies a by-id read. Upserts what came back, evicts what did not, and leaves every
  // cursor untouched.
  private async applyRows(response: IssueSyncRowsResponse): Promise<void> {
    const now = this.clock.now();

    for (const id of response.removedIds) {
      // A close is confirmed by the row being gone. Any other pending write on a row
      // that has gone has nothing left to confirm against and nothing to roll back to,
      // so it is abandoned rather than reported as failed — the row it patched is no
      // longer on screen to look wrong.
      if (!this.pendingWrites.settle(id, null, true)) this.pendingWrites.abandon([id]);
    }

    const upserts: RowEnvelope<IssueSyncRow>[] = [];
    for (const row of response.rows) {
      this.pendingWrites.settle(row.id, row, false);
      upserts.push({ issueId: row.id, row, touchedAtMs: now });
    }

    if (upserts.length || response.removedIds.length) {
      await this.db.applyBatch(upserts, response.removedIds, null);
      await this.enforceResidency();
      // Patched on the way out, so the caller's own action survives a delta that would
      // otherwise un-apply it.
      this.events.onRowsApplied?.(
        upserts.map(envelope => this.pendingWrites.patched(envelope.row)), response.removedIds,
      );
      for (const status of this.statuses.values()) {
        this.events.onScopeChanged?.(
          status.scopeKey, upserts.map(envelope => envelope.issueId), response.removedIds,
        );
      }
    }

    // A moved scopeVersion means the caller's permitted groups changed. Reconciliation
    // is the only thing that finds a row that left scope without a removal.
    for (const [key, status] of this.statuses) {
      if (status.scopeVersion && status.scopeVersion !== response.scopeVersion) {
        this.lastReconciledAt.delete(key);
      }
    }
  }

  // Rolls back any write whose deadline passed. Driven from the same tick as the poll.
  expireWrites(): Promise<void> {
    return this.serialize(() => this.rollBackExpired());
  }

  // Settles shadows for writes this tab made and another tab confirmed. Only the
  // elected writer applies deltas, so without this a write issued from any other tab
  // would never see its confirmation and would roll back as failed after landing.
  //
  // Absence is read from the reported removals, never from the row missing on disk: an
  // evicted store makes every row absent, and treating that as confirmation would
  // settle every pending write at once.
  settleApplied(rowIds: string[], removedIds: string[]): Promise<void> {
    return this.serialize(async () => {
      for (const id of removedIds) this.pendingWrites.settle(id, null, true);
      for (const id of rowIds) {
        if (!this.pendingWrites.isPending(id)) continue;
        const stored = await this.db.getRow(id);
        if (stored) this.pendingWrites.settle(id, stored.row, false);
      }
    });
  }

  // Drops everything for this tenant. Used on sign-out; a tenant switch lands on a
  // different database and needs no purge to stay correct.
  async purge(): Promise<void> {
    await this.serialize(async () => {
      this.pendingWrites.clear();
      this.statuses.clear();
      this.lastReconciledAt.clear();
      await this.db.clear();
    });
  }

  private readonly lastReconciledAt = new Map<string, number>();
  private reconciling = false;

  private serialize<T>(work: () => Promise<T>): Promise<T> {
    const next = this.chain.then(work, work);
    // Keep the chain alive after a rejection so one failed cycle cannot wedge every
    // later apply behind it.
    this.chain = next.catch(() => undefined);
    return next;
  }

  private async runSync(scope: IssueSyncScope): Promise<SyncOutcome> {
    const scopeKey = scopeKeyOf(scope);
    const stored = await this.db.getSyncState(scopeKey);

    // A scope with no cursor is starting from nothing, so the residency decision
    // happens now, before any of it is pulled.
    if (!stored) {
      const probe = await this.probe(scope);
      if (probe === SyncOutcomes.Denied || probe === SyncOutcomes.Failed) return probe;
      if (probe === SyncOutcomes.Declined) return SyncOutcomes.Declined;
    }

    try {
      return await this.walk(scope);
    } catch (err) {
      if (err instanceof SyncScopeDeniedError) {
        // The scope is refused. Stop syncing it and leave every other scope alone —
        // their rows are still legitimately held.
        this.statuses.delete(scopeKey);
        await this.db.deleteSyncState(scopeKey);
        return SyncOutcomes.Denied;
      }
      if (err instanceof SyncCursorRejectedError) {
        // The stored cursor is not one this server issued. Drop it and walk the
        // scope again rather than retrying a value that can only fail.
        await this.db.deleteSyncState(scopeKey);
        try {
          await this.walk(scope);
          return SyncOutcomes.Synced;
        } catch (retryErr) {
          // A refusal on the retry is still a refusal. Reporting it as a plain failure
          // would leave the scope listed and its state stored, so the next poll would ask
          // again for rows the caller may no longer see.
          if (retryErr instanceof SyncScopeDeniedError) {
            this.statuses.delete(scopeKey);
            await this.db.deleteSyncState(scopeKey);
            return SyncOutcomes.Denied;
          }
          return SyncOutcomes.Failed;
        }
      }
      return SyncOutcomes.Failed;
    }
  }

  // Reads the scope's size without pulling it. One row is enough: scopeTotal is on
  // every response. A full first page would part-fill a scope this may then decline,
  // which would leave the store holding a scope the user cannot see listed.
  private async probe(scope: IssueSyncScope): Promise<SyncOutcome> {
    let response;
    try {
      response = await this.transport.fetchDelta(scope, '', 1);
    } catch (err) {
      if (err instanceof SyncScopeDeniedError) return SyncOutcomes.Denied;
      return SyncOutcomes.Failed;
    }

    // A floor means "at least this many". The server's cap sits above the client's
    // threshold, so a floored count is always over it and the comparison holds
    // without needing the exact number.
    const over = response.scopeTotal > RESIDENCY_LIMIT
      || (response.scopeTotalIsFloor && response.scopeTotal >= RESIDENCY_LIMIT);
    if (!over) return SyncOutcomes.Synced;

    const scopeKey = scopeKeyOf(scope);
    const status: ScopeStatus = {
      scopeKey,
      scope,
      complete: false,
      scopeTotal: response.scopeTotal,
      scopeTotalIsFloor: response.scopeTotalIsFloor,
      overThreshold: true,
      scopeVersion: response.scopeVersion,
      asOfUtc: response.asOfUtc,
    };
    this.statuses.set(scopeKey, status);
    this.events.onStatus?.(status);
    // Nothing is stored and no cursor is kept: the scope was never started.
    return SyncOutcomes.Declined;
  }

  // Walks the feed for one scope until the server reports it complete.
  private async walk(scope: IssueSyncScope): Promise<SyncOutcome> {
    const scopeKey = scopeKeyOf(scope);
    let guard = 0;
    // Bounds the walk at the largest scope the client will hold, so a server that
    // never reports complete cannot spin forever.
    const maxPages = Math.ceil(RESIDENCY_LIMIT / SYNC_PAGE_LIMIT) + 2;

    for (;;) {
      if (this.stopped) return SyncOutcomes.Synced;
      const state = await this.db.getSyncState(scopeKey);
      const cursor = state?.nextCursor ?? '';
      const response = await this.transport.fetchDelta(scope, cursor, SYNC_PAGE_LIMIT);
      // The request above crossed a tenant change if this is now set. The response
      // describes the tenant that is current, not the one this replica belongs to.
      if (this.stopped) return SyncOutcomes.Synced;

      // A changed scopeVersion means the caller's permitted groups moved. Reconcile
      // rather than continuing: the delta cannot deliver a row that came into scope
      // with an older timestamp than the cursor, so the cursor is restarted too.
      if (state && response.scopeVersion !== state.scopeVersion) {
        await this.db.deleteSyncState(scopeKey);
        await this.runReconcile();
        continue;
      }

      await this.applyDelta(scope, response, state);

      if (response.complete) return SyncOutcomes.Synced;
      // A scope can cross the threshold after it was legally started, by growing
      // rather than by being widened. Extending it further would pull rows the
      // residency cap immediately discards, for a list that declines to render — and
      // the eviction would be exactly the silent narrowing the threshold exists to
      // prevent. Stop extending and let the caller report the scope as too wide.
      if (this.statuses.get(scopeKey)?.overThreshold) return SyncOutcomes.Synced;
      if (++guard >= maxPages) return SyncOutcomes.Synced;
    }
  }

  private async applyDelta(
    scope: IssueSyncScope,
    response: { rows: IssueSyncRow[]; removedIds: string[]; nextCursor: string; complete: boolean;
      scopeTotal: number; scopeTotalIsFloor: boolean; scopeVersion: string; asOfUtc: string },
    previous: StoredSyncState | null,
  ): Promise<void> {
    const scopeKey = scopeKeyOf(scope);
    const now = this.clock.now();

    // The server withholds rows newer than its safety lag, so the gap between when it
    // assembled the response and the newest row it chose to send is never smaller than
    // that lag. Reading it here keeps the write timeout correct even if the server's
    // lag is raised, which a compiled-in figure could not survive.
    if (response.rows.length) {
      const newest = response.rows.reduce(
        (latest, row) => Math.max(latest, Date.parse(row.updateDateTimeUtc)), 0,
      );
      this.pendingWrites.observeFeedLag(Date.parse(response.asOfUtc), newest);
    }

    // A removal settles a pending close before the eviction happens, so the shadow
    // does not outlive the row it was protecting.
    for (const id of response.removedIds) this.pendingWrites.settle(id, null, true);

    const upserts: RowEnvelope<IssueSyncRow>[] = [];
    for (const row of response.rows) {
      const id = idOf(row);
      this.pendingWrites.settle(id, row, false);
      // Stored exactly as received. The shadow is applied on the way out, never on the
      // way in: a write that is later rolled back has to leave nothing behind, and a
      // patch written into the database outlives the shadow that justified it. hydrate
      // re-applies live shadows over stored rows for the same reason.
      upserts.push({ issueId: id, row, touchedAtMs: now });
    }

    const state: StoredSyncState = {
      scopeKey,
      // Stored verbatim. On an empty page the server echoes the cursor it was given,
      // which parks the scope exactly where it was.
      nextCursor: response.nextCursor,
      scopeVersion: response.scopeVersion,
      complete: response.complete,
      scopeTotal: response.scopeTotal,
      scopeTotalIsFloor: response.scopeTotalIsFloor,
      asOfUtc: response.asOfUtc,
      lastReconciledAtMs: previous?.lastReconciledAtMs ?? 0,
      scope: [...scope.groupIds].sort().join(','),
      fingerprint: scope.fingerprint,
    };

    await this.writeBatch(upserts, response.removedIds, state);
    await this.enforceResidency();

    const status = this.toStatus(state);
    this.statuses.set(scopeKey, status);
    this.events.onStatus?.(status);
    if (upserts.length || response.removedIds.length) {
      // Patched on the way out, so the caller's own action survives a delta that would
      // otherwise un-apply it.
      this.events.onRowsApplied?.(
        upserts.map(envelope => this.pendingWrites.patched(envelope.row)), response.removedIds,
      );
      this.events.onScopeChanged?.(
        scopeKey, upserts.map(envelope => envelope.issueId), response.removedIds,
      );
    }
  }

  // The rows and the cursor move together in one transaction, so a partly applied
  // delta can never be paired with an advanced cursor. A full disk aborts the
  // transaction rather than failing a request, so the quota is never assumed: space
  // is freed and the write is tried once more.
  private async writeBatch(
    upserts: RowEnvelope<IssueSyncRow>[],
    removedIds: string[],
    state: StoredSyncState,
  ): Promise<void> {
    try {
      await this.db.applyBatch(upserts, removedIds, state);
      return;
    } catch (err) {
      if (!isQuotaExceeded(err)) throw err;
    }
    await this.freeSpace(upserts.length);
    await this.db.applyBatch(upserts, removedIds, state);
  }

  // Drops the coldest rows to make room. Sized against what is being written so one
  // quota failure does not evict the whole store.
  private async freeSpace(incoming: number): Promise<void> {
    const resident = await this.db.countRows();
    const target = Math.max(incoming, Math.ceil(resident * 0.1));
    await this.evictColdest(target);
  }

  // Holds the resident set at the cap. Eviction is by recency of confirmation, so
  // rows in a scope the user has stopped looking at go before rows in the active one.
  private async enforceResidency(): Promise<void> {
    const resident = await this.db.countRows();
    if (resident <= RESIDENCY_LIMIT) return;
    await this.evictColdest(resident - RESIDENCY_LIMIT);
  }

  private async evictColdest(count: number): Promise<void> {
    if (count <= 0) return;
    // Ask for extra: a row with a write in flight is skipped, and dropping it would
    // discard the very state the caller is waiting to have confirmed.
    const pending = new Set(this.pendingWrites.pendingIds());
    const candidates = await this.db.coldestRowIds(count + pending.size);
    const evictable = candidates.filter(id => !pending.has(id)).slice(0, count);
    await this.db.deleteRows(evictable);
  }

  private async runReconcile(): Promise<boolean> {
    // A re-walk inside reconciliation can meet a moved scopeVersion, which asks for a
    // reconcile of its own. One pass is enough; the next cycle picks up the rest.
    if (this.reconciling) return false;
    const scopes = [...this.statuses.values()].filter(status => !status.overThreshold);
    if (!scopes.length) return false;
    this.reconciling = true;
    try {
      return await this.reconcilePass(scopes);
    } finally {
      this.reconciling = false;
    }
  }

  private async reconcilePass(scopes: ScopeStatus[]): Promise<boolean> {

    // The union of every synced scope's id set. Rows are keyed by issue id and carry
    // no scope tag, so a row is only safe to evict once no synced scope claims it.
    // Reconciling one scope in isolation would evict rows another scope legitimately
    // holds.
    const claimed = new Set<string>();
    const now = this.clock.now();
    for (const status of scopes) {
      let response;
      try {
        response = await this.transport.fetchIds(status.scope);
      } catch (err) {
        if (err instanceof SyncScopeDeniedError) {
          // Refused scopes claim nothing, and their rows are no longer permitted.
          this.statuses.delete(status.scopeKey);
          await this.db.deleteSyncState(status.scopeKey);
          continue;
        }
        // A scope whose id set could not be read claims an unknown set. Evicting now
        // would drop rows it still holds, so nothing is evicted this round.
        return false;
      }
      for (const id of response.ids) claimed.add(id);
      this.lastReconciledAt.set(status.scopeKey, now);

      const state = await this.db.getSyncState(status.scopeKey);
      if (state && state.scopeVersion !== response.scopeVersion) {
        // Permission moved while reconciling. Restart the walk so rows that came into
        // scope behind the cursor are still delivered.
        await this.db.deleteSyncState(status.scopeKey);
      } else if (state) {
        await this.db.putSyncState({ ...state, lastReconciledAtMs: now });
      }
    }

    const residentIds = await this.db.getAllRowIds();
    const residentSet = new Set(residentIds);
    const stale = residentIds.filter(id => !claimed.has(id));
    if (stale.length) {
      // Permission removal outranks a write in flight: a row the user may not see
      // leaves even when the shadow still holds a patch for it. The shadow is
      // abandoned rather than failed — the write may well have succeeded, and the
      // user simply can no longer see the row it applied to.
      this.pendingWrites.abandon(stale);
      await this.db.deleteRows(stale);
      // Reconciliation evicts without going through a delta, so a consumer patching
      // from deltas would keep showing a row the server no longer claims — the exact
      // permission failure reconciliation exists to prevent.
      this.events.onRowsApplied?.([], stale);
    }
    // Reconfirming presence keeps a live row from ageing out through the residency
    // cap merely because its own columns have not changed.
    await this.db.touchRows(residentIds.filter(id => claimed.has(id)), now);

    // The id set also names rows the client does not hold. On a scope still filling
    // that is ordinary. On a finished scope it means the cursor passed a row that
    // became visible late — writers stamp their own timestamps, so a row committed
    // earlier can appear afterwards, and no future delta will ever reach back for it.
    // Restarting the walk is what recovers it; the id set alone carries no row data.
    const missing = [...claimed].filter(id => !residentSet.has(id));
    if (missing.length) {
      const finished = await this.finishedScopes(scopes);
      for (const status of finished) {
        await this.db.deleteSyncState(status.scopeKey);
        await this.walk(status.scope).catch(() => undefined);
      }
    }

    // The evicted ids ride along. Reconciliation is the permission mechanism, and only the
    // elected tab runs it — announcing the scope without naming what left would leave every
    // other tab showing a row the server has stopped claiming until it is reloaded.
    for (const status of scopes) this.events.onScopeChanged?.(status.scopeKey, [], stale);
    return true;
  }

  private async finishedScopes(scopes: ScopeStatus[]): Promise<ScopeStatus[]> {
    const finished: ScopeStatus[] = [];
    for (const status of scopes) {
      const state = await this.db.getSyncState(status.scopeKey);
      if (state?.complete) finished.push(status);
    }
    return finished;
  }

  private async rollBackExpired(): Promise<void> {
    const expired = this.pendingWrites.takeExpired();
    if (!expired.length) return;
    // Dropping the shadow restores the stored row, but only in the database — the
    // consumer still holds the patched copy it was handed when the write began, and
    // nothing else in the stream would ever mention these rows again. Re-emit them.
    const envelopes = await this.db.getRows(expired.map(write => write.issueId));
    if (envelopes.length) {
      this.events.onRowsApplied?.(envelopes.map(envelope => envelope.row), []);
    }
    for (const status of this.statuses.values()) this.events.onScopeChanged?.(status.scopeKey);
    this.events.onWriteRolledBack?.(expired);
  }

  private toStatus(state: StoredSyncState): ScopeStatus {
    return {
      scopeKey: state.scopeKey,
      scope: {
        groupIds: state.scope ? state.scope.split(',').filter(id => !!id) : [],
        fingerprint: state.fingerprint,
      },
      complete: state.complete,
      scopeTotal: state.scopeTotal,
      scopeTotalIsFloor: state.scopeTotalIsFloor,
      overThreshold: state.scopeTotal > RESIDENCY_LIMIT
        || (state.scopeTotalIsFloor && state.scopeTotal >= RESIDENCY_LIMIT),
      scopeVersion: state.scopeVersion,
      asOfUtc: state.asOfUtc,
    };
  }
}
